<template>
  <div class="cp">
    <header class="cp-hero">
      <div class="cp-hero__glow" aria-hidden="true"></div>
      <div class="cp-hero__main">
        <h1 class="cp-hero__title">Претензионная работа</h1>
        <p class="cp-hero__lead">
          Срывы, отказы и простои — система раскладывает рейсы сама, считает суммы и сроки претензий,
          формирует и отправляет акты простоя. Ваше дело — сообщить то, что видите только вы.
        </p>
        <div class="cp-hero__stats">
          <span class="cp-stat cp-stat--critical"><b>{{ critical }}</b> требуют действий</span>
          <span v-if="claimMoney" class="cp-stat cp-stat--gold"><b>{{ claimMoney }}</b> к претензии заводу</span>
          <span class="cp-stat"><b>{{ days }}</b> дн. в сводке</span>
        </div>
      </div>
      <div class="cp-hero__actions">
        <button type="button" class="cp-btn" @click="openReport()">Сообщить о простое</button>
        <router-link class="cp-btn cp-btn--ghost" to="/downtimes">Все простои</router-link>
        <router-link class="cp-btn cp-btn--ghost" to="/knowledge?a=claims">Как это работает</router-link>
        <ReportInaccuracy variant="ghost" section="claims" object-type="segment" :object-id="active" :context="{ segment: active }" />
      </div>
    </header>

    <OutboxPanel class="cp-outbox" />
    <LegalCasesPanel class="cp-outbox" />

    <nav class="cp-tabs" aria-label="Разделы претензионной работы">
      <button
        v-for="s in segments"
        :key="s.key"
        type="button"
        :class="['cp-tab', `cp-tab--${s.level}`, { 'is-active': active === s.key, 'is-zero': !s.count }]"
        :title="s.hint"
        @click="select(s.key)"
      >
        <span class="cp-tab__icon" aria-hidden="true">{{ icon(s.key) }}</span>
        <span class="cp-tab__name">{{ s.title }}</span>
        <span class="cp-tab__count">{{ s.count }}</span>
      </button>
    </nav>

    <section v-if="current" class="cp-panel">
      <div class="cp-panel__head">
        <div>
          <h2 class="cp-panel__title">{{ current.title }}</h2>
          <p class="cp-panel__hint">{{ current.hint }}</p>
          <p v-if="subtitle(current)" class="cp-panel__money">{{ subtitle(current) }}</p>
        </div>
        <input v-model="query" class="cp-search" type="search" placeholder="Номер рейса, маршрут, клиент…" aria-label="Поиск" />
      </div>

      <div v-if="error" class="cp-error">
        Не удалось загрузить: {{ error }} <button type="button" class="cp-link" @click="load">Повторить</button>
      </div>
      <div v-else-if="pending && !rows.length" class="cp-skeleton"><span v-for="n in 5" :key="n"></span></div>
      <div v-else-if="!visibleRows.length" class="cp-empty">
        <span aria-hidden="true">🌿</span> Здесь пусто — {{ current.level === 'info' ? 'событий нет' : 'всё в порядке' }}.
      </div>
      <div v-else class="cp-table-wrap">
        <table class="cp-table">
          <thead>
            <tr><th v-for="c in columns" :key="c">{{ c }}</th></tr>
          </thead>
          <tbody>
            <template v-if="current.kind === 'outcome'">
              <tr v-for="r in visibleRows" :key="`o-${r.id}`">
                <td>
                  <router-link class="cp-trip" :to="`/trips/${r.id}`">{{ r.trip_id }}</router-link>
                  <div v-if="r.amount" class="cp-amount">{{ money(r.amount) }}</div>
                  <div v-if="pctLabel(r)" :class="['cp-pct', { 'cp-pct--kib': r.kib }]" :title="r.pct_note || ''">{{ pctLabel(r) }}</div>
                  <div v-if="r.deadline" :class="['cp-deadline', deadlineTone(r.days_left)]">
                    пакет до {{ formatDate(r.deadline) }} · {{ deadlineText(r.days_left) }}
                  </div>
                </td>
                <td>{{ r.route }}<div class="cp-muted">{{ r.site }}</div></td>
                <td>{{ r.client || '—' }}</td>
                <td>{{ formatWhen(r.load_at) }}</td>
                <td>
                  <div>{{ formatWhen(r.event_at) }} <span class="cp-src">{{ r.source === 'mail' ? 'письмо' : 'TMS' }}</span></div>
                  <div class="cp-reason">{{ r.reason }}</div>
                </td>
                <td>
                  {{ r.responsible || '—' }}
                  <div class="cp-row-actions"><ReportInaccuracy section="claims" object-type="trip" :object-id="r.id" :trip-number="r.trip_id" :context="r" /></div>
                </td>
              </tr>
            </template>
            <template v-else-if="current.kind === 'downtime'">
              <tr v-for="r in visibleRows" :key="`d-${r.route_point_id}`">
                <td><router-link class="cp-trip" :to="`/trips/${r.id}`">{{ r.trip_id }}</router-link><div class="cp-muted">{{ r.route }}</div></td>
                <td>{{ r.point }}</td>
                <td>{{ r.site || '—' }}<div class="cp-muted">{{ r.client }}</div></td>
                <td>{{ formatWhen(r.planned_at) }}</td>
                <td>{{ formatWhen(r.arrival_at) }}</td>
                <td>
                  {{ waitText(r.delay_minutes) }}<div v-if="r.reason" class="cp-reason">{{ r.reason }}</div>
                  <div class="cp-row-actions"><ReportInaccuracy section="claims" object-type="downtime" :object-id="r.route_point_id" :trip-number="r.trip_id" :context="r" /></div>
                </td>
              </tr>
            </template>
            <template v-else>
              <tr v-for="r in visibleRows" :key="`r-${r.id}`">
                <td>
                  <router-link v-if="r.cargo_trip_id" class="cp-trip" :to="`/trips/${r.cargo_trip_id}`">{{ r.trip_id }}</router-link>
                  <span v-else>{{ r.trip_id }}</span>
                </td>
                <td>{{ r.point }}<div v-if="r.comment" class="cp-muted">{{ r.comment }}</div></td>
                <td>{{ r.reported_by || '—' }}</td>
                <td>{{ formatWhen(r.created_at) }}</td>
                <td>
                  <span :class="['cp-status', `cp-status--${statusTone(r.status)}`]">{{ r.status_text }}</span>
                  <div class="cp-row-actions"><ReportInaccuracy section="claims" object-type="downtime_report" :object-id="r.id" :trip-number="r.trip_id" :context="r" /></div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </section>

    <teleport to="body">
      <div v-if="reportOpen" class="cp-backdrop" @mousedown.self="closeReport">
        <form class="cp-dialog" role="dialog" aria-modal="true" aria-labelledby="cp-report-title" @submit.prevent="submitReport">
          <h2 id="cp-report-title">Сообщить о простое</h2>
          <p class="cp-dialog__lead">Система сама зарегистрирует простой, сформирует акт и отправит заказчику в нормативное время.</p>
          <label for="cp-trip">Номер рейса</label>
          <input id="cp-trip" ref="tripInput" v-model="form.tripNumber" inputmode="numeric" placeholder="например, 50753495" />
          <span class="cp-label">Где стоим</span>
          <div class="cp-seg" role="radiogroup" aria-label="Где простой">
            <button type="button" :class="['cp-seg__btn', { 'is-active': form.operation === 'loading' }]" @click="form.operation = 'loading'">На погрузке</button>
            <button type="button" :class="['cp-seg__btn', { 'is-active': form.operation === 'unloading' }]" @click="form.operation = 'unloading'">На выгрузке</button>
          </div>
          <label for="cp-comment">Комментарий <span class="cp-muted">(необязательно)</span></label>
          <textarea id="cp-comment" v-model="form.comment" rows="3" maxlength="1000" placeholder="Что происходит на площадке"></textarea>
          <p v-if="formError" class="cp-form-error">{{ formError }}</p>
          <p v-if="result" :class="['cp-result', `cp-status--${statusTone(result.status)}`]">{{ result.status_text }}</p>
          <div class="cp-dialog__actions">
            <button type="button" class="cp-btn cp-btn--ghost" :disabled="sending" @click="closeReport">{{ result ? 'Закрыть' : 'Отмена' }}</button>
            <button v-if="!result" type="submit" class="cp-btn" :disabled="sending">{{ sending ? 'Отправляем…' : 'Сообщить' }}</button>
          </div>
        </form>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchClaimsItems, reportDowntime } from '@/backend/claimsApi'
import OutboxPanel from '@/pages/claims/OutboxPanel.vue'
import LegalCasesPanel from '@/pages/claims/LegalCasesPanel.vue'
import ReportInaccuracy from '@/components/feedback/ReportInaccuracy.vue'
import {
  SEGMENT_ICONS,
  columnsFor,
  deadlineText,
  deadlineTone,
  filterRows,
  formatDate,
  formatMoney,
  formatWhen,
  reportStatusTone,
  segmentSubtitle,
  totalCount,
  validateReport,
  waitText,
  pctLabel,
} from '@/pages/claims/claimsLogic.mjs'

const REFRESH_MS = 2 * 60 * 1000
const route = useRoute()
const router = useRouter()
const data = ref(null)
const pending = ref(false)
const error = ref('')
const query = ref('')
const active = ref(String(route.query.s || ''))
const reportOpen = ref(Boolean(route.query.report))
const sending = ref(false)
const formError = ref('')
const result = ref(null)
const tripInput = ref(null)
const form = reactive({ tripNumber: String(route.query.trip || ''), operation: '', comment: '' })
let timer = null

const segments = computed(() => data.value?.segments || [])
const days = computed(() => data.value?.days ?? 7)
const critical = computed(() => totalCount(segments.value, 'critical'))
const claimMoney = computed(() => {
  const s = segments.value.find((x) => x.key === 'factory_late_cancel')
  return s && s.amount ? formatMoney(s.amount) : ''
})
const current = computed(() => segments.value.find((s) => s.key === active.value) || segments.value[0] || null)
const rows = computed(() => (current.value && data.value?.items?.[current.value.key]) || [])
const visibleRows = computed(() => filterRows(rows.value, query.value))
const columns = computed(() => columnsFor(current.value?.kind))

const icon = (key) => SEGMENT_ICONS[key] || '•'
const subtitle = (s) => segmentSubtitle(s)
const money = (v) => formatMoney(v)
const statusTone = (s) => reportStatusTone(s)

async function load() {
  pending.value = true
  try {
    data.value = await fetchClaimsItems()
    error.value = ''
    if (!active.value || !segments.value.some((s) => s.key === active.value)) {
      const firstBusy = segments.value.find((s) => s.count > 0)
      active.value = (firstBusy || segments.value[0] || {}).key || ''
    }
  } catch (e) {
    error.value = e?.message || 'нет связи'
  } finally {
    pending.value = false
  }
}

function select(key) {
  active.value = key
  query.value = ''
  router.replace({ query: { ...route.query, s: key } }).catch(() => {})
}

function openReport() {
  result.value = null
  formError.value = ''
  reportOpen.value = true
  nextTick(() => tripInput.value?.focus?.())
}

function closeReport() {
  if (sending.value) return
  reportOpen.value = false
  if (result.value) {
    Object.assign(form, { tripNumber: '', operation: '', comment: '' })
    result.value = null
  }
}

async function submitReport() {
  const check = validateReport(form)
  if (!check.ok) {
    formError.value = check.error
    return
  }
  formError.value = ''
  sending.value = true
  try {
    result.value = await reportDowntime({ tripNumber: check.tripNumber, operation: form.operation, comment: form.comment })
    load()
  } catch (e) {
    formError.value = e?.response?.data?.detail || e?.message || 'Не удалось отправить'
  } finally {
    sending.value = false
  }
}

function onKey(e) {
  if (e.key === 'Escape' && reportOpen.value) closeReport()
}

watch(() => route.query.s, (value) => { if (value) active.value = String(value) })

onMounted(() => {
  load()
  timer = setInterval(load, REFRESH_MS)
  window.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => {
  clearInterval(timer)
  window.removeEventListener('keydown', onKey)
})
</script>

<style scoped>
.cp { --gold: #f2b705; --gold-2: #ffe08a; --ink: #1f2430; --muted: #6b7280; --line: #e7e5df;
  --critical: #dc2626; --warning: #d97706; --info: #6b7a99; --calm: #16a34a;
  max-width: 1280px; margin: 0 auto; padding: 24px 20px 80px; display: flex; flex-direction: column; gap: 16px; color: var(--ink); }

.cp-hero { position: relative; overflow: hidden; isolation: isolate; display: flex; justify-content: space-between; gap: 18px;
  flex-wrap: wrap; align-items: flex-end; padding: 26px 28px; border-radius: 24px; color: #fff;
  background: radial-gradient(120% 140% at 0% 0%, #2b3142 0%, #1f2430 45%, #15171d 100%);
  box-shadow: 0 30px 60px -30px rgba(31, 36, 48, .55); }
.cp-hero__glow { position: absolute; right: -6%; top: -60%; width: 520px; height: 520px; border-radius: 50%; z-index: -1;
  background: radial-gradient(circle, rgba(242, 183, 5, .28), rgba(242, 183, 5, 0) 62%); animation: cp-breathe 9s ease-in-out infinite; }
.cp-hero__main { display: flex; flex-direction: column; gap: 10px; max-width: 720px; }
.cp-hero__title { margin: 0; font-size: 36px; letter-spacing: -.02em; line-height: 1.1; }
.cp-hero__lead { margin: 0; color: #c9ccd4; line-height: 1.55; }
.cp-hero__stats { display: flex; gap: 8px; flex-wrap: wrap; }
.cp-stat { padding: 5px 12px; border-radius: 999px; font-size: 13px; color: #e5e7eb; background: rgba(255, 255, 255, .08);
  border: 1px solid rgba(255, 255, 255, .14); }
.cp-stat b { color: #fff; font-size: 15px; margin-right: 4px; }
.cp-stat--critical b { color: #fca5a5; }
.cp-stat--gold { color: var(--gold-2); border-color: rgba(242, 183, 5, .35); background: rgba(242, 183, 5, .12); }
.cp-stat--gold b { color: var(--gold-2); }
.cp-hero__actions { display: flex; gap: 8px; flex-wrap: wrap; }

.cp-btn { display: inline-flex; align-items: center; justify-content: center; padding: 10px 16px; border-radius: 999px; border: 0;
  font: inherit; font-size: 14px; font-weight: 650; text-decoration: none; cursor: pointer; color: var(--ink);
  background: linear-gradient(135deg, var(--gold-2), var(--gold)); transition: transform .2s, box-shadow .2s; }
.cp-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 10px 22px -12px rgba(242, 183, 5, .95); }
.cp-btn:disabled { opacity: .6; cursor: default; }
.cp-btn--ghost { color: inherit; background: rgba(255, 255, 255, .08); border: 1px solid rgba(255, 255, 255, .18); }
.cp-dialog .cp-btn--ghost { color: var(--ink); background: #f7f5ef; border-color: var(--line); }

.cp-tabs { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 8px; }
.cp-tab { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 8px; padding: 10px 12px;
  border-radius: 14px; border: 1px solid var(--line); border-left: 4px solid var(--info); background: #fff; color: var(--ink);
  font: inherit; text-align: left; cursor: pointer; transition: transform .2s, box-shadow .2s, background .2s; }
.cp-tab:hover { transform: translateY(-1px); box-shadow: 0 10px 22px -16px rgba(31, 36, 48, .5); }
.cp-tab--critical { border-left-color: var(--critical); }
.cp-tab--warning { border-left-color: var(--warning); }
.cp-tab.is-zero { opacity: .7; }
.cp-tab.is-active { background: #1f2430; color: #fff; border-color: #1f2430; opacity: 1; }
.cp-tab__icon { font-size: 18px; }
.cp-tab__name { font-size: 13px; line-height: 1.3; }
.cp-tab__count { min-width: 28px; padding: 2px 8px; border-radius: 999px; text-align: center; font-weight: 700; font-size: 13px;
  background: #f3f1ea; color: var(--ink); }
.cp-tab.is-active .cp-tab__count { background: var(--gold); }

.cp-panel { padding: 18px 20px; border-radius: 20px; background: #fff; border: 1px solid var(--line);
  box-shadow: 0 18px 40px -30px rgba(31, 36, 48, .45); display: flex; flex-direction: column; gap: 14px;
  animation: cp-in .35s ease both; }
.cp-panel__head { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; align-items: flex-start; }
.cp-panel__title { margin: 0; font-size: 20px; }
.cp-panel__hint { margin: 4px 0 0; color: var(--muted); font-size: 13.5px; max-width: 720px; }
.cp-panel__money { margin: 6px 0 0; font-weight: 700; color: var(--critical); }
.cp-search { min-width: 260px; padding: 9px 14px; border-radius: 999px; border: 1px solid var(--line); font: inherit; font-size: 14px; }
.cp-search:focus { outline: 2px solid var(--gold-2); border-color: var(--gold); }

.cp-table-wrap { overflow-x: auto; }
.cp-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13.5px; }
.cp-table th { position: sticky; top: 0; text-align: left; font-weight: 600; color: var(--muted); font-size: 12px; text-transform: uppercase;
  letter-spacing: .04em; padding: 8px 10px; border-bottom: 1px solid var(--line); background: #fff; }
.cp-table td { padding: 10px; border-bottom: 1px solid #f1efe9; vertical-align: top; }
.cp-table tbody tr { transition: background .15s; }
.cp-table tbody tr:hover { background: #fbfaf6; }
.cp-trip { font-weight: 700; color: var(--ink); text-decoration: none; border-bottom: 1px dashed var(--gold); }
.cp-amount { margin-top: 4px; font-weight: 700; color: var(--critical); }
.cp-deadline { margin-top: 2px; font-size: 12px; color: var(--muted); }
.cp-deadline.is-soon { color: var(--warning); font-weight: 600; }
.cp-deadline.is-overdue { color: var(--critical); font-weight: 700; }
.cp-muted { color: var(--muted); font-size: 12.5px; }
.cp-reason { margin-top: 4px; color: #4b5563; font-size: 12.5px; line-height: 1.4; max-width: 460px; }
.cp-src { display: inline-block; margin-left: 4px; padding: 0 6px; border-radius: 999px; font-size: 11px; background: #f3f1ea; color: var(--muted); }
.cp-status { display: inline-block; padding: 3px 10px; border-radius: 10px; font-size: 12.5px; line-height: 1.4; }
.cp-status--ok { background: #ecfdf3; color: #166534; }
.cp-status--bad { background: #fef2f2; color: #991b1b; }
.cp-status--muted { background: #f3f4f6; color: #374151; }
.cp-empty { padding: 28px; text-align: center; color: var(--muted); border-radius: 16px; background: #fbfaf7; }
.cp-error { color: var(--critical); }
.cp-link { border: 0; background: none; color: var(--ink); text-decoration: underline; cursor: pointer; font: inherit; }
.cp-skeleton { display: flex; flex-direction: column; gap: 8px; }
.cp-skeleton span { height: 44px; border-radius: 12px; background: linear-gradient(90deg, #f2f0ea, #faf9f5, #f2f0ea);
  background-size: 200% 100%; animation: cp-shimmer 1.4s linear infinite; }

.cp-backdrop { position: fixed; inset: 0; z-index: 2000; display: grid; place-items: center; padding: 16px;
  background: rgba(21, 23, 29, .55); backdrop-filter: blur(3px); animation: cp-fade .2s ease both; }
.cp-dialog { width: min(480px, 100%); display: flex; flex-direction: column; gap: 8px; padding: 22px; border-radius: 20px;
  background: #fff; color: var(--ink); box-shadow: 0 30px 70px -30px rgba(0, 0, 0, .6); animation: cp-pop .25s cubic-bezier(.2, .7, .2, 1) both; }
.cp-dialog h2 { margin: 0; font-size: 20px; }
.cp-dialog__lead { margin: 0 0 6px; color: var(--muted); font-size: 13.5px; }
.cp-dialog label, .cp-label { font-size: 13px; font-weight: 600; margin-top: 4px; }
.cp-dialog input, .cp-dialog textarea { padding: 10px 12px; border-radius: 12px; border: 1px solid var(--line); font: inherit; font-size: 14px; }
.cp-dialog input:focus, .cp-dialog textarea:focus { outline: 2px solid var(--gold-2); border-color: var(--gold); }
.cp-seg { display: inline-flex; padding: 4px; border-radius: 999px; background: #f3f1ea; gap: 4px; }
.cp-seg__btn { flex: 1; border: 0; padding: 8px 14px; border-radius: 999px; background: transparent; font: inherit; font-size: 13.5px; cursor: pointer; }
.cp-seg__btn.is-active { background: linear-gradient(135deg, var(--gold-2), var(--gold)); font-weight: 650; }
.cp-form-error { margin: 4px 0 0; color: var(--critical); font-size: 13px; }
.cp-result { margin: 6px 0 0; padding: 10px 12px; border-radius: 12px; }
.cp-dialog__actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }

@keyframes cp-breathe { 50% { transform: scale(1.08); opacity: .8; } }
@keyframes cp-in { from { opacity: 0; transform: translateY(6px); } }
@keyframes cp-fade { from { opacity: 0; } }
@keyframes cp-pop { from { opacity: 0; transform: translateY(10px) scale(.98); } }
@keyframes cp-shimmer { to { background-position: -200% 0; } }
@media (max-width: 640px) { .cp-hero__title { font-size: 28px; } .cp-search { min-width: 0; width: 100%; } }
@media (prefers-reduced-motion: reduce) { .cp-hero__glow, .cp-panel, .cp-dialog, .cp-backdrop { animation: none; } }
.cp-pct { margin-top: 2px; font-size: 12px; color: #6b7280; }
.cp-pct--kib { color: #b42318; font-weight: 700; }
.cp-row-actions { margin-top: 6px; }
</style>
